import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, Check, FileBadge } from 'lucide-react';
import { Button } from '../components/ui/button';
import { registrationServices } from '../data/services';
import ServiceCard from '../components/common/ServiceCard';

const Registration = () => {
  const registrationBenefits = [
    'End-to-end registration assistance',
    'All government liaison handled',
    'Expert documentation support',
    'Quick processing guaranteed',
    'Post-registration compliance support',
    'Affordable and transparent pricing',
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="registration-page">
      <Helmet>
        <title>Business Registration Services - Trademark, FSSAI, IEC | GK Legal Solutions</title>
        <meta name="description" content="Startup India, Udyam, IEC, Trademark, FSSAI License, 12A/80G, PSARA License registration services. Expert assistance for all business registrations." />
        <link rel="canonical" href="https://gklegalsolutions.com/registration" />
      </Helmet>
      {/* Hero */}
      <section className="py-16 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Registration Services</span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mt-3 mb-6">
                Business Registrations & Licenses
              </h1>
              <p className="font-body text-lg text-slate-600 leading-relaxed mb-8">
                From Startup India to FSSAI, Trademark to PSARA - we help you obtain all essential business registrations and licenses to operate legally across India.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button className="btn-primary flex items-center gap-2" data-testid="registration-get-started-btn">
                    Get Started
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="tel:+919820669943">
                  <Button variant="outline" className="btn-secondary flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Free Consultation
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800"
                alt="Legal documents"
                className="rounded-2xl shadow-2xl w-full h-[400px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <FileBadge className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-heading text-2xl font-bold text-slate-900">9+</p>
                    <p className="font-body text-sm text-slate-600">Registration Types</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="section-spacing bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              Our Registration Services
            </h2>
            <p className="font-body text-lg text-slate-600">
              Complete registration solutions for businesses across all sectors.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {registrationServices.map((service) => (
              <ServiceCard key={service.id} service={service} linkTo="/contact" />
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-spacing bg-slate-50">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <p className="font-heading text-3xl font-bold text-primary mb-1">1000+</p>
                  <p className="font-body text-sm text-slate-600">Registrations Done</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <p className="font-heading text-3xl font-bold text-primary mb-1">100%</p>
                  <p className="font-body text-sm text-slate-600">Success Rate</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <p className="font-heading text-3xl font-bold text-primary mb-1">Quick</p>
                  <p className="font-body text-sm text-slate-600">Processing</p>
                </div>
                <div className="bg-white p-6 rounded-xl shadow-md">
                  <p className="font-heading text-3xl font-bold text-primary mb-1">Expert</p>
                  <p className="font-body text-sm text-slate-600">Team Support</p>
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <span className="font-body text-sm font-semibold text-primary uppercase tracking-wider">Why Choose Us</span>
              <h2 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mt-3 mb-8">
                Hassle-Free Registration Services
              </h2>
              <div className="space-y-4">
                {registrationBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-4 h-4 text-green-600" />
                    </div>
                    <p className="font-body text-slate-700">{benefit}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing bg-primary">
        <div className="container-custom text-center">
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-white mb-6">
            Need a Business Registration?
          </h2>
          <p className="font-body text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Our experts will guide you through the entire registration process. Get started today!
          </p>
          <Link to="/contact">
            <Button className="bg-white text-primary hover:bg-slate-100 px-8 py-3 font-semibold">
              Start Registration
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Registration;
